import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CircleCheck, Info, PlugZap, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Button } from '@/deploy/components/ui/Button';
import * as api from './profiler.api';
import { PERIODS, type EventList, type PeriodHours, type Series, type ServerDetail } from './profiler.types';
import { SPIKE_THRESHOLD_MS, TARGET_TPS, TICK_BUDGET_MS, budgetText, levelHealth, serverHealth, startingUp } from './profiler.health';
import { notFoundKind, type NotFoundKind } from './profiler.servers';
import { POLL_MS, eventsScope, longestTickHint, periodChoices, periodTitle, readServerPage, seriesDue, spikesHourTile } from './profiler.page';
import { fmtAgo, fmtBytes, fmtClock, fmtMs, fmtNumber, fmtTps, fmtWhen, plural, stepLabel } from './profiler.format';
import { BudgetMeter, Note, StageBars, StatTile, StatusLight, ThreadBars, cx } from './ProfilerParts';
import { SeriesTable, SpikeColumns, TickChart, budgetInFrame } from './ProfilerCharts';
import { EventTable } from './ProfilerEvents';
import s from './Profiler.module.scss';

// ============================================================
// Déploiement > Profileur > un serveur
// ============================================================
// Pour un serveur : son voyant et la phrase qui l'explique, la durée du tick au fil du temps, où part
// ce temps (étape par étape), les pics avec leur heure locale et leur cause en clair, et les fils du
// serveur les plus gourmands. Le monde et la période se choisissent en haut, une fois, pour tout ce
// qui suit : la courbe, les étapes ET les pics sont ceux du monde affiché.
//
// Relève toutes les 30 secondes pour le voyant et les pics. La courbe, elle, n'est relue qu'au rythme
// où elle change (un point par minute sur une heure, par 30 minutes sur 7 jours, cf profiler.page.ts).
// ============================================================

const EVENTS_LIMIT = 100;

export function ProfilerServerPage() {
  const { serverKey = '' } = useParams<{ serverKey: string }>();
  const [params, setParams] = useSearchParams();
  const group = params.get('g');
  const hours = (PERIODS as readonly number[]).includes(Number(params.get('p'))) ? (Number(params.get('p')) as PeriodHours) : 1;
  const levelParam = params.get('monde');

  const [detail, setDetail] = useState<ServerDetail | null>(null);
  const [series, setSeries] = useState<Series | null>(null);
  /** Les pics affichés, avec le monde pour lequel ils ont été demandés (null : tous les mondes). */
  const [events, setEvents] = useState<{ list: EventList; level: string | null } | null>(null);
  const [error, setError] = useState<{ message: string; kind: NotFoundKind | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const run = useRef(0);
  /** Dernière courbe lue : pour quelle demande, quand, et pour quel monde. */
  const lastSeries = useRef<{ query: string; at: number; level: string | null } | null>(null);

  const load = useCallback(async (force: boolean) => {
    const mine = ++run.current;
    setLoading(true);
    const query = `${serverKey}|${group ?? ''}|${hours}|${levelParam ?? ''}`;
    const kept = lastSeries.current?.query === query ? lastSeries.current : null;
    const read = await readServerPage(api, { key: serverKey, group, hours, levelParam }, {
      withSeries: force || seriesDue(kept?.at ?? null, hours, Date.now()),
      shownLevel: kept?.level ?? null,
      eventsLimit: EVENTS_LIMIT,
    });
    if (mine !== run.current) return;
    setLoading(false);
    const d = read.detail;
    if (!d.data) {
      setError({ message: d.error ?? 'Réponse illisible', kind: d.status === 404 ? notFoundKind(d.error) : 'error' });
      return;
    }
    setError(null);
    setDetail(d.data);
    // Une lecture en échec garde ce qui était affiché : la courbe ne clignote pas sur une erreur passagère.
    if (read.series?.data) {
      setSeries(read.series.data);
      lastSeries.current = { query, at: Date.now(), level: read.series.data.level };
    }
    if (read.events.data) setEvents({ list: read.events.data, level: read.eventsLevel });
  }, [serverKey, group, hours, levelParam]);

  useEffect(() => {
    void load(false);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(false); }, POLL_MS);
    return () => { run.current++; window.clearInterval(timer); };
  }, [load]);

  // Autre serveur : rien de l'ancien ne reste à l'écran. Autre période ou autre monde : la courbe
  // précédente reste, estompée, jusqu'à l'arrivée de la nouvelle.
  useEffect(() => {
    setDetail(null);
    setSeries(null);
    setEvents(null);
    setError(null);
    lastSeries.current = null;
  }, [serverKey, group]);

  const setParam = (name: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value == null) next.delete(name); else next.set(name, value);
    setParams(next, { replace: true });
    setHover(null);
  };

  const back = (
    <Link to="/deploiement/profileur" className={s.back}><ArrowLeft size={15} aria-hidden /> Tous les serveurs</Link>
  );

  if (!detail) {
    return (
      <div className={s.page}>
        {back}
        {error ? (
          <Note tone={error.kind === 'error' ? 'bad' : 'info'} icon={error.kind === 'error' ? PlugZap : Info}
            title={error.kind === 'moduleOff' ? "Le profileur n'est pas activé sur ce satellite"
              : error.kind === 'unavailable' ? "Le profileur n'est pas encore disponible sur ce satellite"
                : error.kind === 'missing' ? 'Aucune mesure reçue de ce serveur' : 'Mesures illisibles'}>
            {error.kind === 'moduleOff'
              ? 'Le module « profiler » du satellite est éteint : aucune mesure n\'y est gardée.'
              : error.kind === 'unavailable'
                ? 'Ce satellite est d\'une version d\'avant le profileur : il ne connaît pas encore ces mesures. Ce n\'est pas une panne.'
                : error.kind === 'missing'
                  ? 'Ce serveur n\'a jamais envoyé de mesure, ou elles sont trop anciennes pour être encore gardées.'
                  : error.message}
          </Note>
        ) : <p className={s.empty}>Lecture des mesures…</p>}
      </div>
    );
  }

  const { server, latest, now } = detail;
  const health = serverHealth(server, now, undefined, detail.startupGraceSeconds);
  const measuring = health.state === 'measuring';
  // Un serveur qui vient de démarrer n'est pas jugé : ses chiffres s'affichent, sans couleur.
  const starting = measuring && startingUp(server, now, detail.startupGraceSeconds);
  const levels = series?.levels.length ? series.levels : server.levels.map((l) => l.level);
  const level = series?.level ?? levelParam ?? server.levels[0]?.level ?? null;
  const current = server.levels.find((l) => l.level === level) ?? null;
  const currentHealth = measuring && current && !starting ? levelHealth(current) : null;
  const periods = periodChoices(detail.pointsRetentionDays);
  const periodName = periodTitle(hours, detail.pointsRetentionDays);
  const scope = events ? eventsScope(events.level, levels.length) : { label: null, levelColumn: false };
  const seriesStale = loading || (series != null && (series.hours !== hours || (levelParam != null && series.level !== levelParam)));
  const totals = series?.totals ?? null;
  const hasPoints = (series?.points.length ?? 0) > 0;
  const hasSpikes = (series?.points ?? []).some((p) => p.spikes > 0);
  const expectedMinutes = series ? Math.round((series.to - series.from) / 60_000) : 0;
  const spikesTile = spikesHourTile(server, starting);

  return (
    <div className={s.page}>
      {back}
      <PageHeader
        eyebrow="Profileur"
        title={server.name}
        subtitle={[
          server.model,
          server.group,
          server.engine ? `moteur ${server.engine}` : null,
          server.startedAt ? `démarré ${startedText(server.startedAt, now)}` : null,
        ].filter(Boolean).join(' · ')}
        actions={(
          <>
            {server.lastSnapshotAt && <span className={s.updated}>Dernières mesures {fmtAgo(server.lastSnapshotAt, now)}</span>}
            <Button size="sm" leftIcon={<RefreshCw size={14} />} loading={loading} onClick={() => void load(true)}>Rafraîchir</Button>
          </>
        )}
      />

      <div className={cx(s.banner, s.card)}>
        <StatusLight health={health} showReason size="lg" />
      </div>

      {error && (
        <Note tone="bad" icon={PlugZap} title="La dernière lecture a échoué">
          {error.message} Les chiffres ci-dessous sont ceux de la lecture précédente.
        </Note>
      )}

      {/* Un seul rang de réglages, au-dessus de tout ce qu'il commande. */}
      <div className={s.filters}>
        <div className={s.filterGroup} role="group" aria-label="Période">
          <span className={s.filterLabel}>Période</span>
          <div className={s.filterOptions}>
            {periods.map((p) => (
              <button key={p.hours} type="button" className={cx(s.filterOption, p.hours === hours && s.filterActive)} aria-pressed={p.hours === hours}
                onClick={() => setParam('p', p.hours === 1 ? null : String(p.hours))}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {levels.length > 1 && (
          <label className={s.filterGroup}>
            <span className={s.filterLabel}>Monde</span>
            <select className={s.select} value={level ?? ''} onChange={(e) => setParam('monde', e.target.value)}>
              {levels.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        )}
        {levels.length === 1 && <span className={s.filterNote}>Monde : <strong>{levels[0]}</strong></span>}
      </div>

      {/* ---- Maintenant : la dernière minute ---- */}
      {measuring && current && (
        <section className={s.section} aria-label="Dernière minute">
          <h2 className={s.sectionTitle}>Maintenant <span className={s.sectionSub}>sur la dernière minute</span></h2>
          <div className={s.tiles}>
            <StatTile label="Ticks par seconde" value={fmtTps(current.ticksPerSecond)} hint={`${TARGET_TPS} attendus`} />
            <StatTile label="Durée d'un tick" value={current.ticks > 0 ? fmtMs(current.averageTickMs) : '-'}
              hint={current.ticks > 0 ? `en moyenne, soit ${budgetText(current.averageTickMs)} (${TICK_BUDGET_MS} ms)` : 'aucun tick fini'}>
              <BudgetMeter averageMs={current.averageTickMs} maxMs={current.ticks > 0 ? current.maxTickMs : null} light={currentHealth?.light ?? 'none'} />
            </StatTile>
            <StatTile label="Tick le plus long" value={current.ticks > 0 ? fmtMs(current.maxTickMs) : '-'} hint={longestTickHint(current)} />
            <StatTile label="Mémoire par tick" value={fmtBytes(current.bytesPerTick)}
              hint={current.gcPauseMs > 0 ? `${fmtMs(current.gcPauseMs)} de nettoyage sur la minute` : 'aucun nettoyage sur la minute'} />
            <StatTile label="Retard" value={fmtMs(current.lagMs)} hint="à rattraper pour tenir le rythme" />
            <StatTile label="Pics (1 heure)" value={spikesTile.value} hint={spikesTile.hint} />
          </div>
        </section>
      )}

      {/* ---- La période choisie ---- */}
      <section className={cx(s.section, seriesStale && s.stale)} aria-label={periodName}>
        <h2 className={s.sectionTitle}>
          {periodName}
          {series && <span className={s.sectionSub}>{stepLabel(series.stepSeconds)}{totals ? `, ${coverageText(totals.minutes, expectedMinutes)}` : ''}</span>}
        </h2>

        {series == null ? <p className={s.empty}>Lecture de la courbe…</p> : !hasPoints ? (
          <Note icon={Info} title="Aucune mesure sur cette période">
            {measuring
              ? 'Les premières mesures viennent d\'arriver : la courbe se remplit à raison d\'un point par minute.'
              : `Ce serveur n'a envoyé aucune mesure sur cette période.${hours < 168 ? " Une période plus longue montre ses dernières mesures, s'il en reste." : ''}`}
          </Note>
        ) : (
          <>
            <div className={s.card}>
              <header className={s.cardHead}>
                <h3 className={s.cardTitle}>Durée moyenne d'un tick</h3>
                <p className={s.cardSub}>
                  En millisecondes. {budgetInFrame(series)
                    ? `La ligne marque le budget : au-dessus de ${TICK_BUDGET_MS} ms, le jeu ralentit.`
                    : `Le budget de ${TICK_BUDGET_MS} ms est bien au-dessus du cadre : la durée en reste loin.`}
                  {totals?.avgMs != null && ` Moyenne de la période : ${fmtMs(totals.avgMs)}.`}
                </p>
              </header>
              <TickChart series={series} hover={hover} onHover={setHover} />
            </div>

            <div className={s.card}>
              <header className={s.cardHead}>
                <h3 className={s.cardTitle}>Ticks trop longs</h3>
                <p className={s.cardSub}>
                  Nombre de ticks de plus de {SPIKE_THRESHOLD_MS} ms, {stepLabel(series.stepSeconds)}.
                  {totals && totals.spikes > 0 && totals.maxMs != null && ` ${plural(totals.spikes, 'tick trop long', 'ticks trop longs')} sur la période, le plus long de ${fmtMs(totals.maxMs)}.`}
                </p>
              </header>
              {hasSpikes ? <SpikeColumns series={series} hover={hover} onHover={setHover} /> : (
                <p className={cx(s.empty, s.emptyGood)}><CircleCheck size={16} aria-hidden /> Aucun tick trop long sur la période.</p>
              )}
              <SeriesTable series={series} />
            </div>

            <div className={s.twoCols}>
              <div className={s.card}>
                <header className={s.cardHead}>
                  <h3 className={s.cardTitle}>Où part le temps d'un tick</h3>
                  <p className={s.cardSub}>
                    {totals?.avgMs != null ? `Sur la période, un tick dure ${fmtMs(totals.avgMs)} en moyenne. ` : ''}
                    Part de ce temps prise par chaque étape, la plus lourde d'abord.
                  </p>
                </header>
                <StageBars stages={series.stages} subStages={series.subStages} averageMs={totals?.avgMs ?? null} />
              </div>
              <div className={s.card}>
                <header className={s.cardHead}>
                  <h3 className={s.cardTitle}>Fils les plus gourmands</h3>
                  <p className={s.cardSub}>
                    Un fil est une file de travail du serveur. 100 % veut dire un cœur du processeur occupé sans arrêt. Dernier relevé, quelle que soit la période.
                  </p>
                </header>
                {measuring
                  ? <ThreadBars process={latest?.process ?? null} />
                  : <p className={s.empty}>Les fils ne sont montrés que pour un serveur dont les mesures arrivent : ce relevé-ci daterait.</p>}
              </div>
            </div>
          </>
        )}
      </section>

      <section className={s.section} aria-label="Pics">
        <h2 className={s.sectionTitle}>
          Pics
          <span className={s.sectionSub}>
            les {EVENTS_LIMIT} plus récents, heure de ce navigateur
            {scope.label ? `, ${scope.label}` : ''}
          </span>
        </h2>
        <div className={s.card}>
          {events == null ? <p className={s.empty}>Lecture des pics…</p> : (
            <EventTable events={events.list.events} now={now} multiLevel={scope.levelColumn}
              level={levels.length > 1 ? events.level : null} />
          )}
        </div>
        <p className={s.footnote}>
          Le moteur détaille au plus 6 pics par minute et par monde, et le satellite en garde 60 par heure et par serveur : la courbe
          « Ticks trop longs » les compte tous, cette liste montre ceux qui ont été détaillés.
        </p>
      </section>
    </div>
  );
}

/** « à 06:00 » le jour même, « le 12/10 à 06:00 » sinon. */
function startedText(t: number, now: number): string {
  const when = fmtWhen(t, now);
  return when.includes(' à ') ? `le ${when}` : `à ${fmtClock(t)}`;
}

/** Part de la période couverte par des mesures : dit s'il y a des trous, sans les cacher. */
function coverageText(minutes: number, expected: number): string {
  if (expected <= 0) return '';
  if (minutes >= expected - 2) return 'mesures complètes';
  return `${plural(minutes, 'minute mesurée', 'minutes mesurées')} sur ${fmtNumber(expected)}`;
}
